import { HamburgerIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { get } from 'lodash';
import { useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';

import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';

import { isRoleRevelioAdmin, useGetLoggedInUser } from '@revelio/auth';
import { FeatureFlag, Loading } from '@revelio/core';
import { USER_DETAILS_QUERY, type User } from '@revelio/data-access';

import { FieldGroup } from '../../../shared/ui/admin/groups/field/field-group';
import { useUnleashFlag } from '../../../shared/unleash-flags/use-unleash-flag';
import { useDeleteUser } from '../hooks/use-delete-user';
import UserForm, { submitEditUser, type UserFormValues } from '../user-form';
import { ClientDetailsQuery, UpdateUserMutation } from '../userOperations';
import UserApiKeyManagement from './user-api-key-management';

const AdminUserEdit = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const userId = searchParams.get('userId');
  const [{ fetching, data }] = useQuery({
    query: USER_DETAILS_QUERY,
    variables: { ids: [userId as string] },
  });
  const userToEdit: User = get(data, 'users[0]', {});
  const [{ fetching: fetchingClient, data: clientData }] = useQuery({
    query: ClientDetailsQuery,
    variables: { ids: [userToEdit.client_name as string] },
    pause: !userToEdit.client_name,
  });

  const [, updateUser] = useMutation(UpdateUserMutation);

  const userFormMethods = useForm<UserFormValues>();

  useEffect(() => {
    if (userId !== userToEdit.id) {
      userFormMethods.reset();
    }
  }, [userId, userToEdit.id, userFormMethods]);

  const {
    loggedInUser,
    query: [{ fetching: loggedInUserLoading }],
  } = useGetLoggedInUser();
  const isRevelioAdmin = isRoleRevelioAdmin(loggedInUser.role);
  const isAdminApiAccessEnabled = useUnleashFlag(FeatureFlag.AdminAPIAcess);
  const showApiKeyManagement = isRevelioAdmin && isAdminApiAccessEnabled;
  const {
    isOpen: isApiKeyModalOpen,
    onOpen: onApiKeyModalOpen,
    onClose: onApiKeyModalClose,
  } = useDisclosure();

  const deleteUser = useDeleteUser();

  return (
    <Box h="100%" as="section">
      {!fetching && userToEdit && !fetchingClient && !loggedInUserLoading ? (
        <Flex
          as="form"
          direction="column"
          h="100%"
          onSubmit={userFormMethods.handleSubmit(
            submitEditUser({
              updateUser,
              userId: userId as string,
              toast,
              currentUser: userToEdit,
              loggedInUser,
            })
          )}
        >
          <Box flex="1" minHeight="0" overflowY="auto" py="2">
            <FieldGroup
              title="Edit User"
              description={`Edit user account - id:${userId}`}
            >
              <UserForm
                userToEdit={userToEdit}
                useFormMethods={userFormMethods}
                setPassword={(newPassword: string) =>
                  userFormMethods.setValue('password', newPassword)
                }
                setTrialExpirationDate={(newTrialExpiration: Date) =>
                  userFormMethods.setValue(
                    'trialExpirationDate',
                    newTrialExpiration.toISOString()
                  )
                }
                passwordRules={{
                  minLength: {
                    value: 4,
                    message: 'Minimum length should be 4',
                  },
                }}
              />
            </FieldGroup>
          </Box>
          <Box
            flexShrink={0}
            borderTopWidth="1px"
            borderColor="gray.200"
            py="2"
          >
            <Stack direction="row" spacing={2} align="center">
              <Button
                colorScheme="navyBlue"
                variant="solid"
                size="md"
                onClick={() => {
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.delete('editing');
                  newSearchParams.delete('userId');
                  setSearchParams(newSearchParams);
                }}
              >
                Cancel
              </Button>
              {showApiKeyManagement && userToEdit.id && (
                <Button
                  colorScheme="blue"
                  size="md"
                  onClick={onApiKeyModalOpen}
                >
                  Manage API Keys
                </Button>
              )}
              <Button
                colorScheme="green"
                variant="solid"
                size="md"
                isLoading={userFormMethods.formState.isSubmitting}
                type="submit"
              >
                Update User
              </Button>
              {clientData?.clients && (
                <Button
                  onClick={() => {
                    const newSearchParams = new URLSearchParams(searchParams);

                    newSearchParams.delete('editing');
                    newSearchParams.delete('userId');

                    if (clientData?.clients) {
                      newSearchParams.set(
                        'client',
                        clientData.clients[0]?.client_name ?? ''
                      );
                    }

                    setSearchParams(newSearchParams);
                  }}
                >
                  Back to User List
                </Button>
              )}
              <Spacer />
              {isRevelioAdmin && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Options"
                    icon={<HamburgerIcon />}
                    variant="solid"
                    colorScheme="gray"
                  />
                  <MenuList>
                    <MenuItem
                      color="red"
                      onClick={() => {
                        if (!userId) return;
                        deleteUser(userId, () => {
                          const newSearchParams = new URLSearchParams(
                            searchParams
                          );
                          newSearchParams.delete('editing');
                          newSearchParams.delete('client');
                          setSearchParams(newSearchParams);
                        });
                      }}
                    >
                      Delete
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </Stack>
          </Box>
          {showApiKeyManagement && userToEdit.id && (
            <UserApiKeyManagement
              userId={userToEdit.id}
              isOpen={isApiKeyModalOpen}
              onClose={onApiKeyModalClose}
            />
          )}
        </Flex>
      ) : (
        <Flex justify="center" minHeight="200px">
          <Center>
            <Loading />
          </Center>
        </Flex>
      )}
    </Box>
  );
};

export default AdminUserEdit;
